import React from "react";
import {
  AutoComplete,
  Table,
  Tag,
  Card,
  Spin,
  Empty,
  Select,
  Button,
  message,
} from "antd";
import useSkillMatrixByUser from "./logic/useSkillMatrixByUser";
import SkillMatrixByUserService from "./logic/SkillMatrixByUserService";
import "./SkillMatrixByUser.scss";

const levelOptions = [
  { label: "L1 - Beginner", value: 1 },
  { label: "L2 - Intermediate", value: 2 },
  { label: "L3 - Advanced", value: 3 },
  { label: "L4 - Professional", value: 4 },
];

const SkillMatrixByUser = () => {
  const {
    searchText,
    setSearchText,
    userOptions,
    selectedUser,
    setSelectedUser,
    skills,
    setSkills,
    loading,
    searchingUsers,
    fetchSkills,
    debouncedSearch,
  } = useSkillMatrixByUser();

  const [editedSkills, setEditedSkills] = React.useState([]);


  const handleLevelChange = (value, record) => {
    const updated = skills.map((s) =>
      s.skillName === record.skillName ? { ...s, skillLevel: value } : s
    );
    setSkills(updated);

    const already = editedSkills.find((s) => s.skillName === record.skillName);
    if (!already) {
      setEditedSkills([
        ...editedSkills,
        { skillName: record.skillName, skillLevel: value },
      ]);
    } else {
      const updatedList = editedSkills.map((s) =>
        s.skillName === record.skillName ? { ...s, skillLevel: value } : s
      );
      setEditedSkills(updatedList);
    }
  };

  const handleSave = async () => {
    if (!selectedUser || editedSkills.length === 0) return;

    try {
      await SkillMatrixByUserService.updateUserSkills(
        selectedUser.id,
        editedSkills
      );
      message.success("Skills updated successfully");
      await fetchSkills(selectedUser.id);
      setEditedSkills([]);
    } catch (err) {
      console.error("Update failed:", err);
      message.error("Failed to update skills");
    }
  };
  const handleClearSearch = () => {
    setSearchText("");
    setSelectedUser(null);
    setSkills([]);
    setEditedSkills([]);
  };

  const columns = [
    {
      title: "Skill",
      dataIndex: "skillName",
      key: "skillName",
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      render: (categories) => (
        <div className="category-path-wrapper">
          {Array.isArray(categories) && categories.length > 0 ? (
            categories.map((cat, index) => {
              const path = [
                cat.categoryName,
                cat.subCategory,
                cat.subSubCategory,
              ]
                .filter(Boolean)
                .join(" ➤ ");
              return (
                <Tag key={index} className="category-path-tag">
                  {path}
                </Tag>
              );
            })
          ) : (
            <Tag>-</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Level",
      dataIndex: "skillLevel",
      key: "skillLevel",
      render: (level, record) => (
        <Select
          value={level}
          onChange={(val) => handleLevelChange(val, record)}
          options={levelOptions}
          style={{ width: 160 }}
        />
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (source) => <Tag color="blue">{source}</Tag>,
    },
    {
      title: "Added At",
      dataIndex: "addedAt",
      key: "addedAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div
      className={`skills-matrix-by-user-container ${
        skills.length > 0 ? "expanded" : "compact"
      }`}
    >
      <Card title="Skill Matrix Search">
        <AutoComplete
          style={{ width: 300, marginBottom: 16 }}
          placeholder="Search by user name"
          value={searchText}
          options={userOptions}
          onSearch={debouncedSearch}
          onSelect={(userId, option) => {
            const selected = option.fullUser;
            setSelectedUser(selected);
            setSearchText(option.label);
            fetchSkills(userId);
            setEditedSkills([]);
          }}
          onChange={(value) => {
            setSearchText(value);
            if (value === "") handleClearSearch();
          }}
          allowClear
          onClear={handleClearSearch}
          notFoundContent={
            searchingUsers ? <Spin size="small" /> : "No users found"
          }
          filterOption={false}
        />

        {skills.length > 0 && (
          <div style={{ marginBottom: 16, textAlign: "right" }}>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={editedSkills.length === 0}
            >
              Save Changes
            </Button>
          </div>
        )}

        {selectedUser && !loading && skills.length === 0 ? (
          <div className="empty-skill-placeholder">
            <Empty
              description={`No skills found for ${selectedUser.firstName} ${selectedUser.lastName}`}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={skills}
            rowKey={(record) =>
              `${record.skillName}-${record.addedAt}-${record.source}`
            }
            pagination={{ pageSize: 10 }}
            bordered
          />
        )}
      </Card>
    </div>
  );
};

export default SkillMatrixByUser;
